// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./SpecificationChangeLog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { CircularProgress } from "@mui/material";

// MUI ICONS

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { convertToPST } from "../../../constants/dateTime";
import {
	checkboxSort,
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const SpecificationChangeLog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [specificationChangeLogs, setSpecificationChangeLogs] = useState([]);
	const [specificationChangeLogRows, setSpecificationChangeLogRows] =
		useState([]);
	const [specificationChangeLogColumns, setSpecificationChangeLogColumns] =
		useState([]);

	// Feedback
	const [isLoading, setIsLoading] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		fetchLogs();

		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Specification Change Logs",
			employeeID: employeeID,
			notes: "Accessed the Specification Change Logs page",
		});
	}, []);

	useEffect(() => {
		createRowData();
		createColumnData();
	}, [specificationChangeLogs]);

	// INPUT HANDLERS

	// API FUNCTIONS
	const fetchLogs = async () => {
		setIsLoading(true);

		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/specs/all"
			);

			setSpecificationChangeLogs(response.data);
		} catch (error) {
			console.log(error);
		}

		setIsLoading(false);
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		setSpecificationChangeLogColumns([
			{
				accessorKey: "employee_name",
				header: () => <span>Employee</span>,
				label: "Employee",
				cell: (props) => <p>{props.getValue() || notApplicable}</p>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "spec_title_and_division",
				header: () => <span>Spec</span>,
				label: "Spec",
				cell: ({ row }) =>
					row.original.spec_division && row.original.spec_title ? (
						<div
							style={{ display: "flex", flexDirection: "column" }}
						>
							<span style={{ color: "gray" }}>
								{row.original.spec_division}
							</span>
							<span style={{ color: "black" }}>
								{row.original.spec_title}
							</span>
						</div>
					) : (
						notApplicable
					),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "description",
				header: () => <span>Description</span>,
				label: "Description",
				cell: ({ row }) => (
					<p>{row.original.description || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "spec_type",
				header: () => <span>Type</span>,
				label: "Type",
				cell: ({ row }) => (
					<p>{row.original.spec_type || notApplicable}</p>
				),
				filterType: "checkbox",
				filterOptions: [
					{
						id: "J2 Standard",
						label: "J2 Standard",
					},
					{
						id: "Local",
						label: "Local",
					},
				],
				filterFn: checkboxSort,
			},
			{
				accessorKey: "created_pst",
				header: () => <span>Time</span>,
				label: "Time",
				cell: ({ row }) => (
					<p>{row.original.created_pst || notApplicable}</p>
				),
				filterType: "date",
				filterFn: dateSort,
			},
		]);
	};

	const createRowData = () => {
		setSpecificationChangeLogRows(
			specificationChangeLogs.map((log) => ({
				employee_name: log.employee_name,
				spec_division: log.spec_division,
				spec_title: log.spec_title,
				spec_title_and_division:
					log.spec_division && log.spec_title
						? log.spec_title + " " + log.spec_division
						: null,
				description: log.description,
				spec_parent_id: log.spec_parent_id,
				spec_title: log.spec_title,
				spec_type: !log.spec_parent_id ? "J2 Standard" : "Local",
				created: log.created,
				created_pst: convertToPST(log.created),
			}))
		);
	};

	// RENDER
	return (
		<div className="SpecificationChangeLog">
			<div className="specificationChangeLogHeader">
				<div className="specificationChangeLogHeaderTitle">
					Specification Change Logs
				</div>
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{specificationChangeLogs.length === 0 && (
						<J2NoResultsFound />
					)}
					{specificationChangeLogs.length > 0 &&
						specificationChangeLogColumns.length > 0 &&
						specificationChangeLogRows.length > 0 && (
							<TanStackTable
								columnData={specificationChangeLogColumns}
								rowData={specificationChangeLogRows}
							/>
						)}
				</div>
			)}
		</div>
	);
};

// EXPORT
export default SpecificationChangeLog;
